import React, { type HTMLAttributes } from 'react';
import classNames from 'classnames';

import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { Container, Text } from '@uncinc/uncinc-react-kitchen-sink';

import { ButtonWrapper, DynamicMediaItem } from '../../../components';
import type { ParagraphTextMedia as Type } from '../../../types/uncinc_profile/paragraphs';
import { TEXT_BACKGROUND_COLOR_BEIGE, TEXT_BACKGROUND_COLOR_GRAY, TEXT_BACKGROUND_COLOR_WHITE } from '../../../config/constants';

import './style.scss';

interface ParagraphTextMediaProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<Type>;
}

const ParagraphTextMedia: React.FC<ParagraphTextMediaProps> = ({
  id,
  entity,
}) => {
  const paragraphTextMedia = entity.toJS();

  const title = paragraphTextMedia.field_title ? paragraphTextMedia.field_title[0]?.value : '';
  const body = paragraphTextMedia.field_body ? paragraphTextMedia.field_body[0]?.processed : '';

  const color = paragraphTextMedia.field_text_background[0].value ?? null;

  const link = paragraphTextMedia.field_link ? paragraphTextMedia.field_link[0] : null;
  const linkTitle = link?.title;
  const linkUrl = link?.alias || link?.uri;

  const linkSecondary =
    paragraphTextMedia.field_link_secondary ? paragraphTextMedia.field_link_secondary[0] : null;
  const linkSecondaryTitle = linkSecondary?.title;
  const linkSecondaryUrl = linkSecondary?.alias || linkSecondary?.uri;

  const ratio = paragraphTextMedia.field_ratio ? paragraphTextMedia.field_ratio[0]?.value : '';

  let alignment = paragraphTextMedia.field_alignment[0].value;
  // Per default, most images have a ratio of 16:9
  // But in many projects, we do a trick where the image is fitted next to the text,
  // depending on the height of the text (when the paragraph is left/right aligned).
  //
  // In those cases, we want to be able to set the `alignmentLeftRightImageRatio` to
  // for example 4:3, so you don't lose too much of your image when it's scaling.
  const imageRatio = alignment === 'top' ? '16_9' : '4_3';

  const media = paragraphTextMedia.field_media_item[0];

  if (!title && !body) {
    alignment = 'top';
  }

  let caption;
  if (media.bundle !== 'video' && media.entity.field_caption) {
    caption = media.entity.field_caption[0]?.value;
  }

  let posterImage;
  if (media.bundle === 'video_file' && media.entity.field_poster_image) {
    posterImage = media.entity.field_poster_image[0]?.url;
  }

  const containerClass = classNames(
    'Paragraph',
    'ParagraphTextMedia',
    `ParagraphTextMedia__alignment__${alignment}`,
    {
      'ParagraphTextMedia--flipped': alignment === 'right',
    },
  );

  const mediaClass = classNames('col-12 col-md-6 col-lg-6', {
    'col-md-10 offset-md-1 col-lg-8 offset-lg-2': alignment === 'top',
  });

  const bodyClass = classNames('col-12 col-md-6 col-lg-6', {
    'col-md-10 offset-md-1 col-lg-8 offset-lg-2': alignment === 'top',
  });


  // TODO make constants for comparison here
  const bodyWrapperClass = classNames('ParagraphTextMedia__body-wrapper', {
    'ParagraphTextMedia__body-wrapper--white': color === TEXT_BACKGROUND_COLOR_WHITE,
    'ParagraphTextMedia__body-wrapper--beige': color === TEXT_BACKGROUND_COLOR_BEIGE,
    'ParagraphTextMedia__body-wrapper--gray': color === TEXT_BACKGROUND_COLOR_GRAY,
    'ParagraphTextMedia__body-wrapper--colored': color !== 'white',
    'ParagraphTextMedia__body-wrapper--top': alignment === 'top',
  });

  return (
    <div id={id} className={containerClass}>
      <Container>
        <div className="row">
          {(title || body || link || linkSecondary) && (
            <div className={bodyClass}>
              <div className={bodyWrapperClass}>
                <div className="ParagraphTextMedia__body">
                  <Text
                    title={title}
                    text={body ?? ''}
                  />
                </div>

                {(link || linkSecondary) && (
                  <div className="ParagraphTextMedia__links">
                    {linkUrl && (
                      <ButtonWrapper
                        className="ParagraphTextMedia__links__link"
                        type="primary"
                        href={linkUrl}
                      >
                        {linkTitle}
                      </ButtonWrapper>
                    )}
                    {linkSecondaryUrl && (
                      <ButtonWrapper
                        className="ParagraphTextMedia__links__link"
                        type="secondary"
                        href={linkSecondaryUrl}
                      >
                        {linkSecondaryTitle}
                      </ButtonWrapper>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className={mediaClass}>
            <div className="ParagraphTextMedia__media-wrapper">
              <DynamicMediaItem
                media={media}
                imageAspectRatio={
                  ratio === 'default'
                    ? imageRatio
                    : 'original'
                }
                className="ParagraphTextMedia__media"
                posterImage={posterImage}
              />
            </div>
            <div className="caption">
              {caption}
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ParagraphTextMedia;
